import { Fragment, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useRouterState } from '@tanstack/react-router';
import { ActionIcon, Center, Group, Loader, Menu, Stack, Text, Tooltip } from '@mantine/core';
import {
  useAuthControllerMe,
  useOrdersControllerNavCounters,
  useAuthControllerLogout,
  useHealthControllerCheck,
  useDocumentsControllerDetail,
} from '@werkbank/shared';
import { useTicketsControllerUnread } from '@werkbank/shared';
import emblemLogo from '../assets/emblem.png';
import { StatusBadge } from './StatusBadge';
import { ClockWidget } from './ClockWidget';
import { HeaderSearch } from './HeaderSearch';
import { WareneingangGlocke } from './WareneingangGlocke';
import { Icon } from './navIcons';
import { NAV_SECTIONS, type NavItem } from './navModel';
import { SLOTS } from '../lib/settingsNav';
import { readJsonLocal, readLocal, writeLocal } from '../lib/storage';

/* D-W1 Welle 2 (SH-3, W2-B1): lokale UI-Präferenzen der Sidebar. localStorage
   kann fehlen oder gesperrt sein (z. B. Safari privat) — dann gilt der
   Standard und die Präferenz ist schlicht nicht persistent. Vollanalyse S6:
   die lokalen readLocal/writeLocal sind in den gemeinsamen Helfer lib/storage
   gewandert (ein Idiom für AppLayout, Kundenliste und Plantafel). */
const SIDEBAR_COLLAPSED_KEY = 'wb.sidebar.collapsed';
// D-W1 Welle 2 (W2-B2): eingeklappte Sidebar-Gruppen (Sektions-Labels als JSON-Array).
const NAV_CLOSED_GROUPS_KEY = 'wb.sidebar.closedGroups';
function readClosedGroups(): readonly string[] {
  const parsed = readJsonLocal(NAV_CLOSED_GROUPS_KEY);
  return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
}

/** Shell gemäß Brief v2.0 §2: Sidebar 280 px (Rail 72), Topbar 68 px. */
export function AppLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // W2-B5: aktiver Texte-Slot, damit die Slot-Ebene im Untermenü markiert.
  const activeSlot = useRouterState({
    select: (s) => (s.location.search as { slot?: string }).slot ?? '',
  });
  // Ein Angebot ist ein Beleg und öffnet unter `/documents/:id` — die Sidebar
  // würde damit „Belegjournal" markieren. Wir spiegeln die Herkunfts-Logik des
  // Beleg-Editors (QUOTE → Angebote, sonst Journal): den Belegtyp aus dem
  // Detail-Cache lesen und beim QUOTE-Beleg auf „Angebote" umlenken. react-query
  // dedupliziert die Abfrage mit dem Editor, also kein zweiter Request.
  const docDetailId = pathname.match(/^\/documents\/([^/]+)$/)?.[1];
  const docDetail = useDocumentsControllerDetail(docDetailId ?? '', {
    query: { enabled: !!docDetailId, retry: false },
  });
  const isQuoteDetail = !!docDetailId && docDetail.data?.type === 'QUOTE';
  // D-W1 Welle 2 (SH-3, W2-B1): der Rail-Zustand überlebt den Reload
  // (Linear-Muster) — reine UI-Präferenz, lokal gespeichert.
  const [collapsed, setCollapsed] = useState(() => readLocal(SIDEBAR_COLLAPSED_KEY) === '1');
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      writeLocal(SIDEBAR_COLLAPSED_KEY, c ? '0' : '1');
      return !c;
    });
  // D-W1 Welle 2 (W2-B2): Sektionen sind einklappbar (Linear-Muster), der
  // Zustand liegt lokal. Im Rail-Zustand bleiben alle Symbole sichtbar —
  // die Gruppen-Präferenz gilt nur für die ausgeklappte Sidebar.
  const [closedGroups, setClosedGroups] = useState<readonly string[]>(readClosedGroups);
  const toggleGroup = (label: string) =>
    setClosedGroups((prev) => {
      const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
      writeLocal(NAV_CLOSED_GROUPS_KEY, JSON.stringify(next));
      return next;
    });

  const me = useAuthControllerMe({ query: { retry: false } });
  /**
   * Marker am Ticket-Eintrag (Faruq 2026-08-24): „Wichtig ist, dass dieser
   * Sidebar-Eintrag einen Marker bekommt, wenn Rückfrage oder Ticket
   * bearbeitet wurde." Gezählt wird, was seit dem letzten Blick des
   * Angemeldeten Bewegung hatte — erledigte Vorgänge zählen nicht.
   *
   * Beim Fensterwechsel neu laden statt kurz zu takten: die Antwort kommt
   * ohnehin per Mail, der Marker ist die Erinnerung beim nächsten Hinsehen.
   * Eine Minute Takt fängt den Fall ab, dass jemand die Anwendung offen
   * stehen lässt.
   */
  const unreadTickets = useTicketsControllerUnread({
    query: { refetchInterval: 30_000, refetchOnWindowFocus: true, staleTime: 10_000 },
  });
  const ticketMarker = unreadTickets.data?.count ?? 0;
  /**
   * Marker an „Aufträge" und „Bestellungen" (Tobias 2026-08-29): überfällige
   * Wiedervorlagen und offener Bestellbedarf. Derselbe Takt wie die Tickets —
   * beides sind Erinnerungen beim Hinsehen, keine Live-Anzeigen.
   */
  /**
   * 30-Sekunden-Takt wie Plantafel und Jetzt-Board (Faruq 2026-08-29). Eigene
   * Aktionen im Web frischen die Zaehler SOFORT auf (zentral im MutationCache,
   * siehe lib/queryClient); der Takt faengt nur, was von aussen kommt — vor
   * allem Meldungen aus der Monteur-App, fuer die es keinen Push-Kanal gibt.
   */
  const navCounters = useOrdersControllerNavCounters({
    query: { refetchInterval: 30_000, refetchOnWindowFocus: true, staleTime: 10_000, retry: false },
  });
  /** Marker am „Support"-Eintrag: nur die eigenen Meldungen. */
  const supportMarker = unreadTickets.data?.mine ?? 0;
  /**
   * „Bin ich Systembetreuung?" kommt vom SERVER, nicht aus den Rechten hier.
   * Für die Ticket-Warteschlange gilt die Hausregel „Administrator-Klasse hält
   * implizit alles" bewusst nicht — sonst sähe Tobias, selbst Administrator,
   * die Warteschlange statt seiner eigenen Meldungen. Eine zweite Ableitung im
   * Web würde genau da auseinanderlaufen.
   */
  const canManageTickets = unreadTickets.data?.isAgent === true;
  const health = useHealthControllerCheck({ query: { retry: false, refetchInterval: 60_000 } });
  const logout = useAuthControllerLogout({
    mutation: {
      onSettled: () => {
        queryClient.clear();
        void navigate({ to: '/login' });
      },
    },
  });

  if (me.isLoading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (me.isError || !me.data) {
    return <Navigate to="/login" />;
  }

  const initials = me.data.displayName
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const isAdmin = me.data.roleIsAdmin;
  /**
   * Sichtbarkeit eines Nav-Eintrags anhand seines Permission-Flags.
   *
   * Früher eine fest verdrahtete Positivliste mit `return false` am Ende —
   * ein neuer Eintrag mit `flag` verschwand dadurch stillschweigend für ALLE
   * (Faruq 2026-08-29: „Zahlungseingänge" fehlte in der Sidebar). Jetzt wird
   * das Flag gegen die echten Rechte des Nutzers geprüft, wie `useHasFlag` es
   * auch tut; die Administrator-Klasse hält implizit alle.
   *
   * `manageTickets` bleibt die Ausnahme: es kommt nicht aus den Flags, sondern
   * aus der Ticket-Abfrage (`isAgent`).
   */
  const flagAllows = (flag?: string): boolean => {
    if (!flag) return true;
    if (flag === 'manageTickets') return canManageTickets;
    if (isAdmin) return true;
    return (me.data.permissions as Record<string, boolean> | undefined)?.[flag] === true;
  };

  const navItem = (item: NavItem) => {
    let active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
    // Beim QUOTE-Beleg unter `/documents/:id` markiert „Angebote" statt
    // „Belegjournal" (siehe `isQuoteDetail`).
    if (isQuoteDetail) {
      if (item.to === '/documents') active = false;
      else if (item.to === '/quotes') active = true;
    }
    const link = (
      <Link
        key={item.to}
        to={item.to}
        // `active` (unten) ist unsere alleinige Quelle für die Markierung: Sie
        // kennt die Belegtyp-Ausnahme (QUOTE → Angebote). Der TanStack-Link
        // markiert per Default aber zusätzlich unscharf jeden Elternpfad —
        // `/documents` läge damit auf `/documents/:id` fälschlich aktiv neben
        // „Angebote". `activeProps` leert diesen Default, damit nur `active` zählt.
        activeProps={{ className: '' }}
        className={`wb-nav-item${active ? ' active' : ''}`}
        style={collapsed ? { justifyContent: 'center', padding: 0 } : undefined}
        data-testid={`nav-${item.to.replace('/', '') || 'home'}`}
      >
        <Icon name={item.icon} />
        {!collapsed && <span>{item.label}</span>}
        {/* Marker: eingeklappt ein Punkt am Symbol, sonst die Zahl rechts.
            Er sitzt an BEIDEN Ticket-Eintraegen — „Tickets" fuer die Betreuung
            und „Support" fuer alle anderen; es ist derselbe Zaehler, nur eine
            andere Menge dahinter. */}
        {(() => {
          // Marker je Eintrag. Die Ticket-Faelle stehen an derselben Stelle in
          // der Leiste; welcher gilt, entscheidet die Rolle (siehe Hilfe-Block).
          const marker =
            item.to === '/tickets'
              ? ticketMarker
              : item.to === '/support'
                ? supportMarker
                : item.to === '/orders'
                  ? (navCounters.data?.ueberfaellig ?? 0)
                  : item.to === '/material'
                    ? (navCounters.data?.bestellbedarf ?? 0)
                    : item.to === '/documents'
                      ? // Tobias 2026-09-09: fertig gemeldete Arbeitsnachweise, Freigabe offen.
                        (navCounters.data?.nachweisePruefung ?? 0)
                      : 0;
          if (marker === 0) return null;
          const text =
            item.to === '/orders'
              ? `${marker} ${marker === 1 ? 'Auftrag ist fällig' : 'Aufträge sind fällig'}`
              : item.to === '/material'
                ? `${marker} ${marker === 1 ? 'Auftrag braucht Material' : 'Aufträge brauchen Material'}`
                : item.to === '/documents'
                  ? `${marker} ${marker === 1 ? 'Arbeitsnachweis fertig gemeldet, Freigabe offen' : 'Arbeitsnachweise fertig gemeldet, Freigabe offen'}`
                  : `${marker} ${marker === 1 ? 'Vorgang wartet' : 'Vorgänge warten'}`;
          return (
            <span
              className={collapsed ? 'wb-nav-dot' : 'wb-nav-badge'}
              aria-label={text}
              title={item.to === '/tickets' || item.to === '/support' ? `${text} auf Sie` : text}
            >
              {collapsed ? '' : marker}
            </span>
          );
        })()}
        {!collapsed && item.children && (
          <span
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              color: 'var(--wb-text-faint)',
              transform: active ? 'rotate(90deg)' : undefined,
              transition: 'transform var(--wb-motion-fast) var(--wb-motion-ease)',
            }}
          >
            <Icon name="chevron" size={14} />
          </span>
        )}
      </Link>
    );
    if (collapsed) {
      return (
        <Tooltip key={item.to} label={item.label} position="right">
          {link}
        </Tooltip>
      );
    }
    if (!item.children || !active) return link;
    // W2-B5 (Faruq): die Einstellungs-Bereiche als Sidebar-Untermenü — offen,
    // solange die Eltern-Route aktiv ist; unter „Texte" die Slot-Ebene.
    const activeTab = pathname.split('/')[2] ?? '';
    return (
      <Fragment key={item.to}>
        {link}
        <div className="wb-nav-subnav" data-testid="settings-subnav">
          {item.children.map((child) => {
            const childActive = activeTab === child.tab;
            return (
              <Fragment key={child.tab}>
                <Link
                  to="/settings/$tab"
                  params={{ tab: child.tab }}
                  className={`wb-nav-subitem${childActive ? ' active' : ''}`}
                  data-testid={`settings-tab-${child.tab}`}
                >
                  <span>{child.label}</span>
                  {child.tab === 'texte' && (
                    <span
                      style={{
                        display: 'inline-flex',
                        transform: childActive ? 'rotate(90deg)' : undefined,
                        transition: 'transform var(--wb-motion-fast) var(--wb-motion-ease)',
                      }}
                    >
                      <Icon name="chevron" size={12} />
                    </span>
                  )}
                </Link>
                {child.tab === 'texte' && childActive && (
                  <div
                    className="wb-nav-subnav wb-nav-subnav-slots"
                    data-testid="texttemplate-slots"
                  >
                    {SLOTS.map((sl) => (
                      <Link
                        key={sl.value}
                        to="/settings/$tab"
                        params={{ tab: 'texte' }}
                        search={{ slot: sl.value }}
                        className={`wb-nav-subslot${activeSlot === sl.value ? ' active' : ''}`}
                        data-testid={`texttemplate-slot-${sl.value}`}
                      >
                        {sl.label}
                      </Link>
                    ))}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </Fragment>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside
        className="wb-sidebar"
        style={{
          width: collapsed ? 'var(--wb-sidebar-rail)' : 'var(--wb-sidebar-width)',
          flex: 'none',
          display: 'flex',
          flexDirection: 'column',
          padding: 'var(--wb-space-md)',
          transition: 'width var(--wb-motion-slow) var(--wb-motion-ease)',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
        data-testid="sidebar"
      >
        <Group
          gap="sm"
          px={collapsed ? 0 : 'xs'}
          py="xs"
          justify={collapsed ? 'center' : 'flex-start'}
        >
          <img
            src={emblemLogo}
            alt="Holzwerkstätte Schroedter"
            className="wb-logo-emblem"
            style={{ width: 32, height: 32 }}
          />
          {!collapsed && (
            <Text fw={600} size="lg">
              Werkbank
            </Text>
          )}
        </Group>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {NAV_SECTIONS.map((section, i) => {
            const items = section.items.filter(
              (it) => (!it.adminOnly || isAdmin) && flagAllows(it.flag),
            );
            if (items.length === 0) return null;
            // D-W1 Welle 2 (W2-B2): Gruppen mit Label sind einklappbar; die
            // Übersicht (ohne Label) und der Rail-Zustand bleiben davon
            // unberührt.
            const closed = !collapsed && !!section.label && closedGroups.includes(section.label);
            return (
              <div
                key={section.label ?? 'home'}
                // Eingeklappt: Gruppen durch feine Trennlinien andeuten (K3, Regel 5).
                style={
                  collapsed && i > 0
                    ? {
                        borderTop: '1px solid var(--wb-surface-hairline)',
                        marginTop: 8,
                        paddingTop: 8,
                      }
                    : undefined
                }
              >
                {!collapsed && section.label && (
                  <button
                    type="button"
                    className="wb-section-toggle"
                    aria-expanded={!closed}
                    onClick={() => toggleGroup(section.label!)}
                    data-testid={`nav-group-${section.label}`}
                  >
                    <span className="wb-section-label">{section.label}</span>
                    <Icon name="chevron" size={12} />
                  </button>
                )}
                {!collapsed && !section.label && <div style={{ height: 4 }} />}
                {!closed && <Stack gap={2}>{items.map(navItem)}</Stack>}
              </div>
            );
          })}
        </div>
        {/* Hilfe über der Trennlinie vor dem Nutzerblock (Faruq 2026-08-20):
            ersetzt den schwebenden Hilfe-Knopf unten rechts. Darunter „Support"
            (Faruq 2026-08-24): der Weg zu den eigenen Meldungen für alle, die
            NICHT die Warteschlange betreuen — die sehen „Tickets" im
            System-Block. Beides führt auf dieselbe Seite; was man dort sieht,
            entscheidet der Server. */}
        <Stack gap={2} data-testid="nav-hilfe-bereich">
          {navItem({ to: '/help', label: 'Hilfe', icon: 'help' })}
          {/* EIN Eintrag, zwei Gesichter (Faruq 2026-08-24): wer die
              Warteschlange betreut, sieht hier „Tickets" und landet in der
              Warteschlange; alle anderen sehen „Support" und ihre eigenen
              Meldungen. Gleiche Stelle, gleiches Symbol — niemand sucht zwei
              Tueren zum selben Raum. */}
          {canManageTickets
            ? navItem({ to: '/tickets', label: 'Tickets', icon: 'ticket' })
            : navItem({ to: '/support', label: 'Support', icon: 'ticket' })}
        </Stack>
        {/* Nutzerblock am Sidebar-Fuß (Faruq 2026-08-17): Avatar + Name +
            Abmelden. Menü öffnet nach oben; eingeklappt nur der Avatar. */}
        <div
          style={{
            borderTop: '1px solid var(--wb-surface-hairline)',
            marginTop: 8,
            paddingTop: 8,
          }}
        >
          <Menu position="top-start" width="target" offset={6} radius="md">
            <Menu.Target>
              <button
                type="button"
                className="wb-user-button"
                data-testid="avatar-menu"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--wb-space-sm)',
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: collapsed ? 6 : '6px 8px',
                  borderRadius: 'var(--wb-radius-nav)',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                }}
              >
                <span className="wb-avatar">{initials}</span>
                {!collapsed && (
                  <Stack gap={0} style={{ minWidth: 0, textAlign: 'left' }}>
                    <Text size="sm" fw={600} truncate data-testid="me-name">
                      {me.data.displayName}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {me.data.roleName}
                    </Text>
                  </Stack>
                )}
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              {/* Faruq 2026-08-17/20: Panel in Sidebar-Breite (width="target"),
                  öffnet nach oben. Hilfe liegt als Nav-Eintrag über dem
                  Nutzerblock — hier nur Profil und Abmelden. */}
              <Menu.Item
                leftSection={<Icon name="users" size={16} />}
                onClick={() => void navigate({ to: '/profile' })}
                data-testid="menu-profile"
              >
                Profil
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<Icon name="logout" size={16} />}
                onClick={() => logout.mutate()}
                data-testid="logout"
              >
                Abmelden
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </aside>

      {/* Topbar + Content */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header
          className="wb-topbar"
          style={{
            height: 'var(--wb-topbar-height)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--wb-space-lg)',
            padding: '0 var(--wb-space-xl)',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          {/* Kundentest 2026-08 (Faruq): Einklappen ganz links, direkt daneben
              die Suche als Symbol — sie wächst erst beim Klick zu einem
              schmalen Feld, statt dauerhaft die halbe Kopfzeile zu belegen. */}
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={toggleCollapsed}
            title="Navigation einklappen"
            data-testid="sidebar-toggle"
          >
            <Icon name="collapse" />
          </ActionIcon>
          <HeaderSearch />
          <div style={{ flex: 1 }} />
          {/* Tobias 2026-09-25: Wareneingänge aus der Monteur-App. */}
          <WareneingangGlocke enabled={flagAllows('manageOrders')} />
          <ClockWidget />
          {/* Systemstatus als sprechendes Badge; die frühere Glocke war ohne
              Funktion (nur Tooltip) und redundant zu diesem Badge — entfernt. */}
          {health.data && health.data.status !== 'ok' && (
            <StatusBadge status="DUE" label="Systemstatus prüfen" />
          )}
          {/* Nutzer-Avatar + Menü liegen jetzt am Sidebar-Fuß (Faruq 2026-08-17). */}
        </header>

        <main className="wb-main">
          <div className="wb-content">{children}</div>
        </main>
      </div>
    </div>
  );
}
